import { createElement } from "./dom_utils.js";
import { SendIcon } from "../icons.js";

/**
 * Transcript panel component with typewriter effect.
 *
 * @param {Object} opts
 * @param {string} opts.employeeName
 * @returns {{ el: HTMLElement, update: Function, destroy: Function }}
 */
export function createTranscriptPanel({ employeeName }) {
  const panel = createElement("div", { className: "sm-transcript-panel" });
  const messagesContainer = createElement("div", { className: "sm-transcript-messages" });
  panel.appendChild(messagesContainer);

  // Suggestions container
  const suggestionsContainer = createElement("div", { className: "sm-suggestions sm-suggestions--hidden" });
  panel.appendChild(suggestionsContainer);

  // Input bar
  const inputBar = createElement("div", { className: "sm-transcript-input-bar sm-transcript-input-bar--hidden" });
  const input = createElement("input", { className: "sm-transcript-input" });
  input.setAttribute("type", "text");
  input.setAttribute("placeholder", "Type a message...");
  const sendBtn = createElement("button", { className: "sm-transcript-send" });
  sendBtn.innerHTML = SendIcon;
  inputBar.appendChild(input);
  inputBar.appendChild(sendBtn);
  panel.appendChild(inputBar);

  let sendCallback = null;

  function handleSend() {
    const text = input.value.trim();
    if (!text) return;
    input.value = "";
    if (sendCallback) sendCallback(text);
  }

  sendBtn.addEventListener("click", handleSend);
  input.addEventListener("keydown", (e) => {
    if (e.key === "Enter") {
      e.preventDefault();
      handleSend();
    }
  });

  let renderedCount = 0;
  let typewriterTimer = null;
  let displayedText = "";
  let isTyping = false;
  let latestAssistantEl = null; // The <span> holding typewriter text
  let latestCursorEl = null;    // The blinking cursor <span>
  let prevMessages = [];

  function buildEmptyState() {
    const empty = createElement("div", { className: "sm-transcript-empty" });
    const avatar = createElement("div", { className: "sm-transcript-empty-avatar" }, employeeName.charAt(0));
    const nameEl = createElement("p", { className: "sm-transcript-empty-name" }, employeeName);
    const hint = createElement("p", { className: "sm-transcript-empty-hint" }, "Conversation will appear here...");
    empty.appendChild(avatar);
    empty.appendChild(nameEl);
    empty.appendChild(hint);
    return empty;
  }

  function scrollToBottom() {
    requestAnimationFrame(() => {
      messagesContainer.scrollTop = messagesContainer.scrollHeight;
    });
  }

  function buildMessageRow(msg) {
    const isUser = msg.role === "user";
    const row = createElement("div", {
      className: `sm-transcript-row ${isUser ? "sm-transcript-row--user" : "sm-transcript-row--assistant"}`,
    });

    if (isUser) {
      row.appendChild(createElement("div", { className: "sm-transcript-bubble sm-transcript-bubble--user" }, msg.content));
    } else {
      const textEl = createElement("div", { className: "sm-transcript-text" });
      const contentSpan = createElement("span");
      contentSpan.textContent = msg.content;
      textEl.appendChild(contentSpan);
      row.appendChild(textEl);
    }

    return row;
  }

  function startTypewriter(fullText, startFrom) {
    if (typewriterTimer) {
      clearInterval(typewriterTimer);
      typewriterTimer = null;
    }

    if (startFrom >= fullText.length) {
      displayedText = fullText;
      isTyping = false;
      if (latestAssistantEl) latestAssistantEl.textContent = fullText;
      removeCursor();
      scrollToBottom();
      return;
    }

    displayedText = fullText.slice(0, startFrom);
    isTyping = true;
    let charIdx = startFrom;

    if (latestAssistantEl) latestAssistantEl.textContent = displayedText;
    addCursor();

    typewriterTimer = setInterval(() => {
      if (charIdx < fullText.length) {
        charIdx++;
        displayedText = fullText.slice(0, charIdx);
        if (latestAssistantEl) latestAssistantEl.textContent = displayedText;
        scrollToBottom();
      } else {
        clearInterval(typewriterTimer);
        typewriterTimer = null;
        isTyping = false;
        removeCursor();
      }
    }, 18);
  }

  function addCursor() {
    if (latestCursorEl) return;
    if (!latestAssistantEl || !latestAssistantEl.parentNode) return;
    latestCursorEl = createElement("span", { className: "sm-transcript-cursor" });
    latestAssistantEl.parentNode.appendChild(latestCursorEl);
  }

  function removeCursor() {
    if (latestCursorEl) {
      latestCursorEl.remove();
      latestCursorEl = null;
    }
  }

  /**
   * Update the transcript with new messages array.
   * Appends only new messages and runs typewriter on latest assistant message.
   */
  function update(messages) {
    if (!messages || messages.length === 0) {
      if (renderedCount === 0 && messagesContainer.children.length === 0) {
        messagesContainer.appendChild(buildEmptyState());
      }
      return;
    }

    // Remove empty state if present and we now have messages
    if (renderedCount === 0 && messagesContainer.children.length > 0) {
      messagesContainer.innerHTML = "";
    }

    // Find latest assistant index
    let latestAssistantIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === "assistant") {
        latestAssistantIdx = i;
        break;
      }
    }

    // Check if the latest assistant message content changed (streaming update)
    const latestAssistantMsg = latestAssistantIdx >= 0 ? messages[latestAssistantIdx] : null;
    const prevLatestAssistantMsg =
      prevMessages.length > 0
        ? (() => {
            for (let i = prevMessages.length - 1; i >= 0; i--) {
              if (prevMessages[i].role === "assistant") return prevMessages[i];
            }
            return null;
          })()
        : null;

    // Append new messages
    for (let i = renderedCount; i < messages.length; i++) {
      const msg = messages[i];
      const isLatest = i === latestAssistantIdx;

      if (isLatest && msg.role === "assistant") {
        // Build assistant row with typewriter support
        const row = createElement("div", {
          className: "sm-transcript-row sm-transcript-row--assistant",
        });
        const textEl = createElement("div", { className: "sm-transcript-text" });
        const contentSpan = createElement("span");
        contentSpan.textContent = "";
        textEl.appendChild(contentSpan);
        row.appendChild(textEl);
        messagesContainer.appendChild(row);

        // Track for typewriter
        latestAssistantEl = contentSpan;
        removeCursor();
        startTypewriter(msg.content, 0);
      } else {
        messagesContainer.appendChild(buildMessageRow(msg));
      }
    }

    // If no new messages were appended but latest assistant content changed (streaming)
    if (
      messages.length === renderedCount &&
      latestAssistantMsg &&
      prevLatestAssistantMsg &&
      latestAssistantMsg.id === prevLatestAssistantMsg.id &&
      latestAssistantMsg.content !== prevLatestAssistantMsg.content
    ) {
      startTypewriter(latestAssistantMsg.content, displayedText.length);
    }

    renderedCount = messages.length;
    prevMessages = messages.map((m) => ({ ...m }));
    scrollToBottom();
  }

  function setInputVisible(visible) {
    if (visible) {
      inputBar.classList.remove("sm-transcript-input-bar--hidden");
    } else {
      inputBar.classList.add("sm-transcript-input-bar--hidden");
    }
  }

  function onSend(callback) {
    sendCallback = callback;
  }

  function updateSuggestions(items) {
    suggestionsContainer.innerHTML = "";
    if (!items || items.length === 0) {
      suggestionsContainer.classList.add("sm-suggestions--hidden");
      return;
    }

    // Chips with staggered animation
    items.forEach((text, idx) => {
      const chip = createElement("button", { className: "sm-suggestion-chip" }, text);
      chip.style.animationDelay = `${idx * 0.1}s`;
      chip.addEventListener("click", () => {
        if (sendCallback) sendCallback(text);
        updateSuggestions([]);
      });
      suggestionsContainer.appendChild(chip);
    });
    suggestionsContainer.classList.remove("sm-suggestions--hidden");
  }

  function destroy() {
    if (typewriterTimer) {
      clearInterval(typewriterTimer);
      typewriterTimer = null;
    }
    removeCursor();
    panel.remove();
  }

  // Initial render — empty state
  messagesContainer.appendChild(buildEmptyState());

  return { el: panel, update, destroy, setInputVisible, onSend, updateSuggestions };
}
